import { useEffect, useState } from 'react';
import { PageTitle } from '../components/ui';
import { AboutPanel } from './MeetingBits';
import { TicketList } from './TicketList';
import { NewTicket } from './NewTicket';
import { TicketPage } from './TicketPage';

/**
 * Tickets: the lab's work in one place.
 *
 * Two kinds of work land here — the action items raised in meetings and the
 * requests people file on their own — and both are a title, an owner, a due
 * date and done. My tickets lists them together; New ticket files one
 * outside a meeting. The two are sidebar entries, not a tab strip: the
 * sidebar already says where you are, so a second row of the same names was
 * noise. A ticket opens as its own page in place of the list, the way a
 * meeting does, with a back link. The former Operations screen (SLA clocks,
 * coverage, who is away) was retired on 2026-10-09; the reply clocks still
 * show on Messages. docs/api-contract-tickets.md has the rules.
 */

export type TicketsTab = 'mine' | 'new';

/** The page is the App's, so each sidebar entry lands on its own one. */
export function Tickets({
  tab,
  onTab,
  openTicket = null,
  onOpenMeeting,
  onOpenRedcap,
}: {
  tab: TicketsTab;
  onTab: (next: TicketsTab) => void;
  /** A ticket to land on, from the bell or a toast; read when the screen mounts (App remounts it to open another). */
  openTicket?: string | null;
  onOpenMeeting: (meetingId: string) => void;
  /** A ticket made from a REDCap flag opens that study's week. */
  onOpenRedcap?: (study: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(openTicket);

  // The New ticket entry always shows the form, even with a ticket open behind it.
  useEffect(() => {
    if (tab === 'new') setOpenId(null);
  }, [tab]);

  if (tab === 'mine' && openId) {
    return <TicketPage id={openId} onBack={() => setOpenId(null)} onOpenRedcap={onOpenRedcap} />;
  }

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageTitle sub="Requests and action items: who owns each, and when it is due. Action items raised in meetings land here too.">
        Tickets
      </PageTitle>

      <AboutPanel storageKey="enact.tickets.about" title="About tickets">
        A ticket is a title, an owner, a due date and done. <strong>New ticket</strong> files one outside a meeting,
        in your own name; its priority sets the due date by business days, weekends and Yale holidays skipped. Action items from
        meetings show up in <strong>My tickets</strong> beside them, marked <em>Meeting</em>, and are settled from
        their meeting. Anyone can file a request, add an update and attach a file. Whoever filed a ticket can reword it
        and close or reopen it, and the person it is assigned to can mark it done, drop it or reopen it. Assigning,
        setting the priority or due date, and closing anyone else's ticket need the update permission; without it, a
        new ticket goes in unassigned at normal priority for whoever runs the queue to pick up. Refer to a participant
        by record ID, never by name.
      </AboutPanel>

      {tab === 'mine' && <TicketList onOpenTicket={setOpenId} onOpenMeeting={onOpenMeeting} />}
      {tab === 'new' && (
        <NewTicket
          onCreated={(id) => {
            onTab('mine');
            setOpenId(id);
          }}
        />
      )}
    </div>
  );
}
